import { IconButton, } from "@bracket/ui/components";
import { getTabularRasaStore, type RasaColumnOptions, } from "./RasaStore";

import type { ReactNode, SyntheticEvent, } from "react";

export declare type RasaSortIconProps<T extends Record<string, any>> = {
	id: string;
	column: RasaColumnOptions<T>;
	field: string;
};

export const RasaSortIcon = <T extends Record<string, any>>({ id, column, field, }: RasaSortIconProps<T> ): ReactNode => {

	if(!column.sortable) return null;
	if(!id) {
		throw new Error("TabularRasa id is required");
	}
	const store = getTabularRasaStore<T>(id)();
	const { ready, sorting_asc, sorting_desc, sortOn, sort, } = store;
	
	// If sort prop is a string matching this field, ALWAYS show descending (user requirement)
	// This overrides any state that might be incorrect
	const sortStr = sort !== undefined ? String(sort) : "";
	const fieldStr = String(field);
	const isDefaultStringSort = typeof sort === "string" && sortStr === fieldStr;
	
	// Debug logging (remove after fixing)
	if(field === "epoch" && ready) {
		console.log("RasaSortIcon debug:", {
			field,
			sort,
			sortType: typeof sort,
			sortStr,
			fieldStr,
			isDefaultStringSort,
			sorting_asc: sorting_asc.includes(field),
			sorting_desc: sorting_desc.includes(field),
		});
	}
	
	// Determine sorting direction
	// For string sorts, default to descending, but allow toggling
	// Only force "desc" if it's a default string sort AND state hasn't been set yet
	const hasExplicitState = sorting_asc.includes(field) || sorting_desc.includes(field);
	const sorting: "asc" | "desc" = hasExplicitState
		? (sorting_desc.includes(field) ? "desc" : "asc") // Use explicit state if set
		: (isDefaultStringSort ? "desc" : "desc"); // Default to desc for string sorts, or just desc

	const handleClick = (evt: SyntheticEvent<HTMLDivElement, Event> ) => {
		evt.preventDefault();
		evt.stopPropagation();
		// If currently desc, toggle to asc; if asc, toggle to desc (never go to null)
		const currentDirection = sorting_asc.includes(field) ? "asc" : "desc";
		sortOn(field, currentDirection === "desc" ? "asc" : "desc");
	};

	const cstyle = {
		cursor: "pointer",
		// Chevron naturally points up, so: 0deg = up (asc), 180deg = down (desc)
		// But user wants desc to show down, so we reverse: desc = 180deg, asc = 0deg
		transform: `rotate(${sorting === "desc" ? "180deg" : "0deg"})`,
		maxHeight: "28px",
		maxWidth: "28px",
		padding: "0rem",
		scale: 0.6,
	};

	const icon = "Chevron";

	return <IconButton icon={icon} style={{...cstyle}} onClick={handleClick} />;
};
